/**
 * The handshake, as a hook. Section 14's figure 8 from the frame's side.
 *
 * ## Where the token lives
 *
 * In a ref, and nowhere else. Not `localStorage`, not `sessionStorage`, not a cookie, not a URL. That is
 * the whole reason section 14 chose postMessage: mobile browsers partition storage for content inside a
 * third-party frame, so anything we persisted might not be there next time - and a credential that
 * survives a reload is a credential somebody can find. The host re-posts it after a reload, which is
 * cheap and is the design.
 *
 * A ref rather than state, deliberately: a refresh mid-upload must swap the token without re-rendering
 * the tree, because a re-render would unmount the component holding the in-flight request.
 *
 * ## The refresh
 *
 * `expiring` goes out about sixty seconds before the token lapses and the host brokers a replacement.
 * Section 14 is explicit that the widget asks *before* failing rather than failing and recovering, so a
 * rep mid-upload is never interrupted - and that is why the old token stays usable until the new one
 * arrives, instead of being cleared when the warning is sent.
 *
 * "Before" is what `expiresIn` on `init` buys: without a lifetime to count back from, the warning can only
 * be armed as a fixed delay *after* each token, which is what shipped and is not what section 14 describes
 * (DG.38, 2026-08-06).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyHostTheme } from '@iqz-ddds/components';
import { HOST_TO_WIDGET } from '@iqz-ddds/contracts';
import type { WidgetAction, WidgetChrome, WidgetTabs, WidgetView } from '@iqz-ddds/contracts';
import { framingOrigin, isAllowedOrigin, outbound, parseHostMessage, postToHost } from './protocol';

/** Section 14: "about 60 seconds ahead". A lead time subtracted from the lifetime, not a delay after it. */
const WARN_BEFORE_MS = 60_000;

/**
 * Post the cue that asks the host for a token, which is the one outbound message that cannot wait for a
 * target to be known (2026-08-06).
 *
 * Every other message goes to the single origin an accepted `init` arrived from, and that is the right rule
 * for all three of them: a height, a lapse warning and an error with a correlation id each go to one checked
 * place or nowhere at all. `ready` is the message that *asks* for that `init`, so at the moment it is sent
 * there may be nothing to have learned. Under a referrer the frame seeds its target from it and this is a
 * single post; under `Referrer-Policy: no-referrer` it had no target, sent nothing, and the host waited for a
 * cue that never came while the panel eventually claimed it had not been given access. A real CRM is entitled
 * to send no referrer, so that was a defect in the bootstrap rather than in any one host.
 *
 * Offering it to each allowlisted origin is safe in a way a wildcard is not, and both halves matter. The
 * browser delivers a targeted post only to a parent whose origin matches, so this is at most one message
 * received however long the list is - and section 14's ban on `"*"` stands, which is what stops the cue being
 * delivered to whatever happens to be in the parent slot. It is also the only message with nothing in it: no
 * token, no height, no correlation id. An origin already trusted to frame us learns nothing from it that it
 * does not already know, having framed us.
 */
function announceReady(target: string | undefined, allowedOrigins: readonly string[]): void {
  if (target !== undefined) {
    postToHost(outbound.ready(), target);
    return;
  }
  for (const origin of allowedOrigins) postToHost(outbound.ready(), origin);
}

export type SessionState =
  /** No `init` yet. The host may take a moment to broker the first token. */
  | { readonly status: 'waiting' }
  | {
      readonly status: 'ready';
      readonly exchangeId: string | undefined;
      /** Whether the panel draws its own header. The host decides; see widgetInitMessageSchema. */
      readonly chrome: WidgetChrome;
      /**
       * Which way the panel's tab strip runs, defaulted here rather than downstream.
       *
       * The contract promises horizontal when the host says nothing, so exactly one place should apply
       * that promise - and this is the place that reads the message.
       */
      readonly tabs: WidgetTabs;
      /**
       * Which tab opens, left undefined when the host has no opinion - and the asymmetry with `tabs`
       * above is deliberate rather than an oversight.
       *
       * `tabs` has a contractual fallback and this does not: which panel a representative lands on is the
       * panel's own decision, and choosing one here would pre-empt a judgement this hook has no business
       * making. It only carries what the host asked for.
       */
      readonly view: WidgetView | undefined;
    }
  /**
   * No usable token and no prospect of one: the frame was loaded by an origin the allowlist does not
   * name, or the host sent nothing at all. Distinct from `waiting` because it is not going to resolve.
   */
  | { readonly status: 'refused'; readonly detail: string };

export interface HostSession {
  readonly state: SessionState;
  /** The current token, read at request time so a refresh mid-flight is picked up. */
  token(): string | undefined;
  /** Report a failure to the host. Never carries a token. */
  reportError(code: string, correlationId?: string): void;
  /** Ask for a replacement now, rather than waiting for the timer. Used when a call answers 401. */
  requestRefresh(): void;
  /**
   * Tell the host how tall the content is now.
   *
   * A verb rather than a destination, and that is the whole point of it. `useReportedHeight` used to
   * resolve its own target from `document.referrer`, so a host serving its page under
   * `Referrer-Policy: no-referrer` completed the handshake, rendered, and was then never told what height
   * to be - a silently unsized iframe, which reads as a broken panel rather than as a policy interaction
   * (DG.38, 2026-08-06). The allowlist decision now lives in one place, and handing a caller a
   * `postMessage` destination is precisely what `postToHost`'s required-target design exists to prevent.
   */
  reportHeight(height: number): void;
}

export interface HostSessionOptions {
  readonly allowedOrigins: readonly string[];
  /**
   * A host-driven action, for a host that draws its own controls.
   *
   * Held in a ref inside the hook, so a caller passing an inline arrow does not have to memoise it -
   * the message listener is attached once and must not be torn down and rebuilt on every render, or a
   * message arriving in the gap is lost.
   */
  readonly onAction?: (action: WidgetAction) => void;
  /** Overridable so a test can drive the timer without waiting fifteen minutes. */
  readonly warnBeforeMs?: number;
}

export function useHostSession({ allowedOrigins, onAction, warnBeforeMs = WARN_BEFORE_MS }: HostSessionOptions): HostSession {
  const token = useRef<string | undefined>(undefined);
  const [state, setState] = useState<SessionState>({ status: 'waiting' });

  /*
   * The action handler, in a ref rather than in the listener's dependencies.
   *
   * The `message` listener is attached once and must stay attached: tearing it down and rebuilding it
   * whenever a caller passes a fresh arrow would drop any message arriving in the gap, and `init` is
   * the one that matters most. So the callback is read at call time instead.
   */
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;

  /**
   * The single origin we will talk to.
   *
   * Resolved once, from the referrer, and narrowed to the allowlist. When the host suppresses its
   * referrer this stays undefined until the first accepted `init`, and that message's own origin becomes
   * the target - so an outbound message always goes to a concrete origin that has already been checked.
   *
   * `ready` is the exception, and it has to be: it is what asks for the `init` this would be learned from,
   * so it cannot be waited on. See `announceReady`, which is the only thing that sends without consulting
   * this ref, and which still sends to allowlisted origins only.
   */
  const target = useRef<string | undefined>(
    (() => {
      const framed = framingOrigin();
      return framed !== undefined && isAllowedOrigin(framed, allowedOrigins) ? framed : undefined;
    })(),
  );

  const warnTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * How long a token lives, in milliseconds, as the host reported it on `init`.
   *
   * Undefined until a host sends `expiresIn`, and undefined means exactly the cadence that shipped before
   * DG.38 - which is what makes counting back from a lifetime non-breaking. Set from `init` alone: `init`
   * carries the whole configuration and a host is told to repeat every field on a re-post, so a re-post
   * without `expiresIn` releases the lifetime the same way one without `theme` releases the theme.
   */
  const lifetimeMs = useRef<number | undefined>(undefined);

  const send = useCallback((message: Parameters<typeof postToHost>[0]) => {
    if (target.current !== undefined) postToHost(message, target.current);
  }, []);

  const requestRefresh = useCallback(() => {
    send(outbound.expiring());
  }, [send]);

  const reportHeight = useCallback(
    (height: number) => {
      send(outbound.resize(height));
    },
    [send],
  );

  const reportError = useCallback(
    (code: string, correlationId?: string) => {
      send(outbound.error(code, correlationId));
    },
    [send],
  );

  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      /*
       * The origin, checked here at the boundary before anything else in this function may run.
       *
       * A `message` handler receives every message the frame is sent: scripts on the host page that have
       * nothing to do with us, a page that framed us without permission, an extension. Without this, `init`
       * is a way to make the widget adopt any token, and `target.current` below is a way to make it send
       * ours somewhere. `parseHostMessage` checks the same thing again and keeps doing so - it is exported
       * and used on its own - but a security check that only exists two calls deep is one that a reader
       * skimming this listener will not see, and one that Sonar's S2819 reports as absent.
       */
      if (!isAllowedOrigin(event.origin, allowedOrigins)) return;

      const message = parseHostMessage(event, allowedOrigins);
      if (message === null) return;

      // The origin is now known to be allowed, so it is safe to answer to. This is what makes the widget
      // work when the host suppresses its referrer, without weakening anything: the allowlist decided.
      target.current = event.origin;

      /*
       * An action carries no token, and has to be handled before anything that assumes one.
       *
       * Falling through would do two silent kinds of damage: `token.current = message.token` would set
       * the live token to undefined, so the next request would go out unauthenticated; and the timer
       * below would rearm, pushing `expiring` a full interval into the future every time a
       * representative pressed one of the host's buttons - so a token could lapse without the warning
       * that exists to prevent exactly that.
       */
      if (message.type === HOST_TO_WIDGET.action) {
        onActionRef.current?.(message.action);
        return;
      }

      token.current = message.token;

      if (message.type === HOST_TO_WIDGET.init) {
        setState({
          status: 'ready',
          exchangeId: message.exchangeId,
          chrome: message.chrome ?? 'full',
          tabs: message.tabs ?? 'horizontal',
          view: message.view,
        });

        /*
         * Seconds on the wire, matching figure 8 and OAuth's own convention, so milliseconds here.
         *
         * Assigned rather than conditionally assigned: a host that stops sending `expiresIn` goes back to
         * the fixed cadence, which is the same rule `applyHostTheme(message.theme)` follows below.
         */
        lifetimeMs.current = message.expiresIn === undefined ? undefined : message.expiresIn * 1_000;

        /*
         * Follow the host's colour scheme (DG.18).
         *
         * `widgetInitMessageSchema` has carried an optional `theme` since stage 8 with a comment saying
         * the widget reads it and applies nothing yet. It applies it now: an embedded panel that stays
         * light inside a CRM somebody has switched to dark reads as a rendering fault rather than as a
         * separate application.
         *
         * The widget deliberately shows no control of its own - two theme switches on one screen, one of
         * them ours, is worse than none - and persists nothing, because the choice belongs to the host
         * page. A rep opening the standalone application later gets their own preference back. Absent
         * means the host said nothing, which falls through to the device.
         */
        applyHostTheme(message.theme);
      }

      /*
       * Rearmed on every token, including a refresh, and armed as a lifetime minus a lead time.
       *
       * `WARN_BEFORE_MS` is named for a lead time and until DG.38 it was applied as a delay: sixty seconds
       * *after* every token rather than sixty before it lapsed. At the shipped 900-second TTL that
       * re-brokered about every minute instead of about every fourteen, fifteen times more often than
       * section 14 describes and against a budget of thirty mints per minute per representative. The
       * comment that used to sit here claimed the frame assumed "the same lifetime it was configured
       * with", which was a configuration that did not exist.
       *
       * Still a relative delay from receipt and never an absolute deadline, for the reason
       * `embedTokenResponseSchema` gives: the host and the browser do not share a clock, and a frame
       * comparing a server's instant against a laptop set five minutes fast would refresh constantly or
       * never. A replacement reuses the lifetime `init` reported, because every mint carries the same
       * configured TTL.
       *
       * The `Math.min` clamp is not decoration. Without it a 30-second token gives
       * `Math.max(1_000, -30_000)` and the frame asks for a replacement every single second.
       */
      const lifetime = lifetimeMs.current;
      const delay =
        lifetime === undefined
          ? Math.max(1_000, warnBeforeMs)
          : Math.max(1_000, lifetime - Math.min(warnBeforeMs, lifetime / 2));

      if (warnTimer.current !== undefined) clearTimeout(warnTimer.current);
      warnTimer.current = setTimeout(() => {
        send(outbound.expiring());
      }, delay);
    }

    globalThis.addEventListener('message', onMessage);

    /*
     * Sent after the listener is attached, and it is not a courtesy.
     *
     * `postMessage` is not queued. A host that posts `init` before this arrives loses it, because there is
     * nothing listening yet - and the frame attaches its listener only after React mounts and after the
     * runtime config has been fetched, which is well after the iframe's own `load` event. The first browser
     * run of the demo host did exactly that: `init` was posted on `load`, the widget never saw it, and the
     * panel showed "not given access" while the host's log claimed both `init` and `ready`.
     *
     * So this message is the host's cue to send the token, and `democrm/public/app.js` treats it that way.
     *
     * Not through `send`, which posts to a learned target or to nowhere: there is nothing learned yet when the
     * cue goes out, and a host that suppresses its referrer would therefore never be asked. See
     * `announceReady`.
     */
    announceReady(target.current, allowedOrigins);

    return () => {
      globalThis.removeEventListener('message', onMessage);
      if (warnTimer.current !== undefined) clearTimeout(warnTimer.current);
    };
  }, [allowedOrigins, send, warnBeforeMs]);

  /**
   * A frame nobody claimed.
   *
   * If no allowlisted origin has spoken within a few seconds, say so on the screen rather than showing a
   * spinner forever. The likeliest cause is a host origin missing from the allowlist, and a rep looking at
   * a permanently loading panel raises it as "the widget is broken".
   */
  useEffect(() => {
    if (state.status !== 'waiting') return undefined;
    const timer = setTimeout(() => {
      setState((current) =>
        current.status === 'waiting'
          ? {
              status: 'refused',
              detail: 'This panel was not given access by the page containing it.',
            }
          : current,
      );
    }, 5_000);
    return () => clearTimeout(timer);
  }, [state.status]);

  /*
   * Memoised, and that is a fix rather than a tidiness (DG.38, 2026-08-06).
   *
   * A fresh object literal every render gave every consumer a new session identity every render, and two
   * of them key work on it: `useReportedHeight` tore down and rebuilt its `ResizeObserver` each time - its
   * comment claimed that only happened on a remount, and the comment was wrong - and `App`'s `useMemo`
   * constructed a new `ApiClient`, which is the object holding the refresh machinery.
   *
   * Sound because there is nothing else to change: all three callbacks are `useCallback`-stable and
   * `token` reads a ref, so `state` is the only member whose identity ever legitimately moves.
   */
  return useMemo(
    () => ({
      state,
      token: () => token.current,
      reportError,
      requestRefresh,
      reportHeight,
    }),
    [state, reportError, requestRefresh, reportHeight],
  );
}

/**
 * Tell the host how tall the content is, whenever it changes.
 *
 * An iframe has no intrinsic height, so without this the host either clips the panel or leaves a fixed
 * gap. A `ResizeObserver` rather than a one-off measurement after render, because the height changes when
 * a checklist grows, when an upload card appears, and when a phone rotates.
 *
 * It takes no allowlist, and losing that parameter is the DG.38 fix rather than a signature tidy-up: this
 * hook used to re-derive its own target from `document.referrer` on every observation, so a host under
 * `Referrer-Policy: no-referrer` - whose handshake succeeds, because the accepted `init`'s own origin is
 * the target for everything else - never received a single `resize`. It goes through `session.reportHeight`
 * now, which is the same checked target `expiring`, `error` and `ready` use.
 */
export function useReportedHeight(session: HostSession, target: React.RefObject<HTMLElement | null>): void {
  /*
   * The last height posted, so an observation that changes nothing sends nothing.
   *
   * This matters more since the tab strip landed: one panel is visible at a time, so the content height
   * moves on every tab click rather than once after load, and a `ResizeObserver` fires for observations
   * that round to the height the host already has.
   */
  const lastReported = useRef<number | undefined>(undefined);
  const { reportHeight } = session;

  useEffect(() => {
    const element = target.current;
    if (element === null) return undefined;

    const observer = new ResizeObserver((entries) => {
      const height = Math.ceil(entries[0]?.target.scrollHeight ?? element.scrollHeight);
      if (height === lastReported.current) return;
      lastReported.current = height;
      reportHeight(height);
    });

    observer.observe(element);
    return () => observer.disconnect();
    // Both dependencies are stable - `reportHeight` is a `useCallback` behind a memoised session object, and
    // the ref is the caller's - so the observer is built on mount and on a genuine remount, and not on every
    // render as it was before the session return was memoised.
  }, [reportHeight, target]);
}
